import { AxiosResponse } from 'axios';

import DTOOperator from '../../types/Admin/DTOOperator';
import CropConditionData from '../../types/CreditRequest/CropConditionData';
import { ResponseList, ResponseListData } from '../../types/ResponseData';
import DTOCprModel from '../../types/Survey/DTOCprModel';
import DTOSurvey from '../../types/Survey/DTOSurvey';
import DTOSurveyRequest from '../../types/Survey/DTOSurveyRequest';
import DTOSurveyType from '../../types/Survey/DTOSurveyType';
import { HighRiskSurveyData } from '../../types/Survey/HighRiskSurveyData';
import RegistrationData from '../../types/Survey/RegistrationData';
import { formatValuesToFormData } from '../form';
import api from './api';

export default {
  cpr: {
    get: async (params: object): Promise<ResponseList<DTOCprModel>> => {
      return api.get(`monitoring/cpr`, params);
    },
    show: async (id: string): Promise<ResponseListData<DTOCprModel>> => {
      return api.get(`monitoring/cpr/${id}`);
    },
    all: async (params: object): Promise<any> => {
      return api.get(`monitoring/cpr/all`, params);
    },
    update: async (id: string, params: object): Promise<any> => {
      return api.put(`monitoring/cpr/${id}`, params);
    },
    store: async (params: object): Promise<any> => {
      return api.post(`monitoring/cpr`, params);
    },
    destroy: async (id: string): Promise<any> => {
      return api.delete(`monitoring/cpr/${id}`);
    },
  },
  registration: {
    get: async (params: object): Promise<ResponseList<RegistrationData>> => {
      return api.get(`monitoring/registration`, params);
    },
    show: async (id: string): Promise<ResponseListData<RegistrationData>> => {
      return api.get(`monitoring/registration/${id}`);
    },
    all: async (params: object): Promise<AxiosResponse<RegistrationData[]>> => {
      return api.get(`monitoring/registration/all`, params);
    },
    update: async (id: string, params: object): Promise<any> => {
      return api.put(`monitoring/registration/${id}`, params);
    },
    store: async (params: object): Promise<any> => {
      return api.post(`monitoring/registration`, params);
    },
    destroy: async (id: string): Promise<any> => {
      return api.delete(`monitoring/registration/${id}`);
    },
    listReport: async (registration: string, params: object): Promise<any> => {
      return api.get(`monitoring/report/${registration}`, params);
    },
    generateReport: async (registration: string, params: object): Promise<any> => {
      return api.post(`monitoring/report/${registration}`, params);
    },
  },
  situation: {
    get: async (params: object): Promise<any> => {
      return api.get(`monitoring/registration/situation`, params);
    },
    all: async (params: object): Promise<any> => {
      return api.get(`monitoring/registration/situation/all`, params);
    },
    update: async (id: string, params: object): Promise<any> => {
      return api.put(`monitoring/registration/situation/${id}`, params);
    },
    store: async (params: object): Promise<any> => {
      return api.post(`monitoring/registration/situation`, params);
    },
    destroy: async (id: string, params: object): Promise<any> => {
      return api.delete(`monitoring/registration/situation/${id}`, params);
    },
  },
  owner: {
    get: async (params: object): Promise<any> => {
      return api.get(`monitoring/owner`, params);
    },
    all: async (params: object): Promise<any> => {
      return api.get(`monitoring/owner/all`, params);
    },
    update: async (id: string, params: object, formData = false): Promise<any> => {
      let paramsApi;
      if (formData) {
        const data = new FormData();
        paramsApi = formatValuesToFormData(params, data);
        paramsApi.append('_method', 'PUT');
      } else {
        paramsApi = {
          ...params,
          _method: 'PUT',
        };
      }
      return api.put(`monitoring/owner/${id}`, params);
    },
    store: async (params: object): Promise<any> => {
      return api.post(`monitoring/owner`, params);
    },
    destroy: async (id: string, params: object): Promise<any> => {
      return api.delete(`monitoring/owner/${id}`, params);
    },
  },
  farm: {
    get: async (params: object): Promise<any> => {
      return api.get(`monitoring/farm`, params);
    },
    all: async (params: object): Promise<any> => {
      return api.get(`monitoring/farm/all`, params);
    },
    update: async (id: string, params: object, formData = false): Promise<any> => {
      let paramsApi;
      if (formData) {
        const data = new FormData();
        paramsApi = formatValuesToFormData(params, data);
        paramsApi.append('_method', 'PUT');
      } else {
        paramsApi = {
          ...params,
          _method: 'PUT',
        };
      }
      return api.put(`monitoring/farm/${id}`, params);
    },
    store: async (params: object): Promise<any> => {
      return api.post(`monitoring/farm`, params);
    },
    destroy: async (id: string, params: object): Promise<any> => {
      return api.delete(`monitoring/farm/${id}`, params);
    },
    destroyContact: async (id: string, params: object): Promise<any> => {
      return api.delete(`monitoring/farm/destroycontact/${id}`, params);
    },
  },
  survey: {
    get: async (params: object): Promise<any> => {
      return api.get(`monitoring/survey`, params);
    },
    show: async (id: string): Promise<any> => {
      return api.get(`monitoring/survey/${id}`);
    },
    find: async (id: string, params: object): Promise<any> => {
      return api.get(`/monitoring/survey/${id}`, params);
    },
    all: async (params: object): Promise<any> => {
      return api.get(`monitoring/survey/all`, params);
    },
    update: async (id: string, params: object, formData = false): Promise<any> => {
      let paramsApi;
      if (formData) {
        const data = new FormData();
        paramsApi = formatValuesToFormData(params, data);
        paramsApi.append('_method', 'PUT');
      } else {
        paramsApi = {
          ...params,
          _method: 'PUT',
        };
      }
      return api.put(`monitoring/survey/${id}`, params);
    },
    store: async (params: object = {}): Promise<any> => {
      return api.post(`monitoring/survey`, params);
    },
    destroy: async (id: string): Promise<any> => {
      return api.delete(`monitoring/survey/${id}`);
    },
    highRisk: async (params: object): Promise<ResponseList<HighRiskSurveyData>> => {
      return api.get(`monitoring/survey/warning`, params);
    },
    approve: async (id: string, params: object): Promise<ResponseList<DTOSurvey>> => {
      return api.patch(`monitoring/survey/${id}/approve`, params);
    },
    stage: {
      get: async (params = {}): Promise<ResponseListData<any>> => {
        return api.get('monitoring/survey/stage', params);
      },
    },
    type: {
      get: async (params: object): Promise<DTOSurveyType> => {
        return api.get(`monitoring/survey/type`, params);
      },
    },
  },
  surveyrequest: {
    getById: async (id: string, params: object): Promise<AxiosResponse<DTOSurveyRequest>> => {
      return api.get(`monitoring/survey-request/${id}`, params);
    },
    getAll: async (params: object): Promise<ResponseList<DTOSurveyRequest>> => {
      return api.get(`monitoring/survey-request`, params);
    },
    update: async (id: string, params: object, formData = false): Promise<any> => {
      let paramsApi;
      if (formData) {
        const data = new FormData();
        paramsApi =
          params instanceof FormData
            ? params
            : formatValuesToFormData({ ...params, _method: 'PUT' }, data, ['documents[]']);
      } else {
        paramsApi = {
          ...params,
          _method: 'PUT',
        };
      }
      return api.post(`monitoring/survey-request/${id}`, paramsApi);
    },
    store: async (payload: object): Promise<any> => {
      return api.post(`monitoring/survey-request`, payload);
    },
    destroy: async (id: string, params: object): Promise<any> => {
      return api.delete(`monitoring/survey-request/${id}`, params);
    },
  },
  cropCondition: {
    get: async (params: object): Promise<any> => {
      return api.get(`monitoring/crop-condition`, params);
    },
    all: async (params: object): Promise<ResponseListData<CropConditionData>> => {
      return api.get(`monitoring/crop-condition/all`, params);
    },
    update: async (id: string, params: object): Promise<any> => {
      return api.put(`monitoring/crop-condition/${id}`, params);
    },
    store: async (params: object): Promise<any> => {
      return api.post(`monitoring/crop-condition`, params);
    },
    destroy: async (id: string, params: object): Promise<any> => {
      return api.delete(`monitoring/crop-condition/${id}`, params);
    },
  },
  operator: {
    get: async (params: object): Promise<ResponseListData<DTOOperator>> => {
      return api.get(`monitoring/operator`, params);
    },
    update: async (id: string, params: object): Promise<any> => {
      return api.put(`monitoring/operator/${id}`, params);
    },
    store: async (params: object): Promise<any> => {
      return api.post(`monitoring/operator`, params);
    },
    destroy: async (id: string, params: object): Promise<any> => {
      return api.delete(`monitoring/operator/${id}`, params);
    },
  },
  typeSurvey: {
    all: async (params: object): Promise<ResponseListData<DTOSurveyType>> => {
      return api.get(`monitoring/survey/type/all`, params);
    },
  },
};
